// Abweichungen der Formularwerte vom gewählten Paket. Zusätze (Zusatzstellen,
// Zusatzzugänge, Zusatzoptionen) stehen als Info da, echte Abweichungen amber.
// „Auf Paket zurücksetzen" entfernt nur die Abweichungen, Zusätze im Rahmen
// des Pakets bleiben.
import {
  ExclamationTriangleIcon,
  PlusCircleIcon
} from '@heroicons/react/20/solid'

import {
  PLANS,
  PLAN_ORDER,
  normalizePlanId
} from '../../constants/pricing-catalog'
import {
  PLAN_NAMES,
  applyPreset,
  derivePresetDiff,
  isUnlimited,
  storageFor
} from '../../helper/pricing'

const toNumber = (value) => {
  if (value === null || value === undefined || value === '') return null
  const n = Number(value)
  return Number.isNaN(n) ? null : n
}

/**
 * Werte ohne Abweichungen vom Paket. Produkte und Mengen außerhalb des Pakets
 * fallen auf das Paket zurück. Zusatzstellen und Zusatzzugänge bis zum
 * Paketmaximum bleiben, ebenso gebuchte Zusatzoptionen und Produkte außerhalb
 * des Preismodells. Der Talentspeicher folgt danach der Formel. custom,
 * „Kein Paket" und unbekannte Pakete bleiben unverändert.
 *
 * @param {object} values Formularwerte
 * @param {string} planId
 * @param {{ available?: string[]|null }} [options]
 */
export const resetToPreset = (values, planId, { available } = {}) => {
  const target = normalizePlanId(planId)
  if (!PLAN_ORDER.includes(target)) return values

  const preset = applyPreset(values, target, { available })
  if (target === 'unlimited') return preset

  const plan = PLANS[target]
  const keep = (value, included, max) => {
    const n = toNumber(value)
    // Unbegrenzt bleibt, wenn das Paket es erlaubt (Zugänge in Growth und
    // Scale), sonst fällt es auf den Paketwert zurück
    const allowed = !isUnlimited(n) || max === Infinity
    return n !== null && allowed && n >= included && n <= max
      ? Math.floor(n)
      : included
  }

  const career_count = keep(
    values?.career_count,
    plan.limits.careers,
    plan.max.careers
  )
  const seat_count = keep(values?.seat_count, plan.limits.seats, plan.max.seats)

  return {
    ...preset,
    career_count,
    seat_count,
    spaces_count: storageFor(target, career_count)
  }
}

/**
 * @param {object} props
 * @param {string|null} props.planId
 * @param {object} props.values Formularwerte
 * @param {string[]|null} [props.available] Produkte der Schema-Probe
 * @param {(nextValues: object) => void} props.onReset
 * @param {boolean} [props.disabled]
 */
const PresetDiff = ({
  planId,
  values,
  available = null,
  onReset,
  disabled = false
}) => {
  const target = normalizePlanId(planId)
  const { customized, items } = derivePresetDiff(values, target, {
    available
  })
  if (!items.length) return null

  const deviations = items.filter(({ kind }) => kind === 'deviation')
  const additions = items.filter(({ kind }) => kind === 'info')

  return (
    <div
      className={
        customized
          ? 'rounded-lg border border-amber-200 bg-amber-50 p-4'
          : 'rounded-lg border border-gray-200 bg-white p-4'
      }
      data-testid='preset-diff'
    >
      <div className='flex flex-wrap items-start justify-between gap-3'>
        <div>
          <h3 className='text-sm font-semibold text-gray-900'>
            {customized
              ? `Abweichungen vom Paket ${PLAN_NAMES[target]}`
              : `Zusätze zum Paket ${PLAN_NAMES[target]}`}
          </h3>
          {customized && (
            <p className='mt-1 text-sm text-gray-600'>
              Die Pricing-Sperre greift mit genau diesen Werten.
            </p>
          )}
        </div>
        {customized && (
          <button
            type='button'
            disabled={disabled}
            onClick={() =>
              onReset(resetToPreset(values, target, { available }))
            }
            className='rounded-md bg-white px-3 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:opacity-40'
          >
            Auf Paket zurücksetzen
          </button>
        )}
      </div>
      <ul className='mt-3 space-y-1.5'>
        {deviations.map(({ field, message }) => (
          <li
            key={`deviation-${field}`}
            className='flex items-start gap-x-2 text-sm text-amber-900'
          >
            <ExclamationTriangleIcon
              className='mt-0.5 h-4 w-4 flex-shrink-0 text-amber-500'
              aria-hidden='true'
            />
            <span>{message}</span>
          </li>
        ))}
        {additions.map(({ field, message }) => (
          <li
            key={`info-${field}`}
            className='flex items-start gap-x-2 text-sm text-gray-700'
          >
            <PlusCircleIcon
              className='mt-0.5 h-4 w-4 flex-shrink-0 text-gray-400'
              aria-hidden='true'
            />
            <span>{message}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

export default PresetDiff
